import {
  Box,
  FieldSelect,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { ALL_PLATFORMS, PlatformType } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Field,
  Form,
  FormErrors,
  formValueSelector,
  GenericField,
  InjectedFormProps,
  reduxForm,
} from 'redux-form';

import { ajaxSend } from 'api/api';
import { useNotifications } from 'hooks/useNotifications';

import { mapPlatform } from '../../platforms';

import { ApnsControls } from './apnsControls';
import { GcmControls } from './gcmControls';

interface SubmitModalOwnProps {
  editPlatform?: PlatformType;
  show: boolean;
  messengerId: string;
  unavailablePlatforms: PlatformType[];
  closeModal: () => void;
  refreshItems: () => void;
}

interface SubmitModalState {
  selectedPlatform: PlatformType | undefined;
  certPassword?: string;
}

interface SubmitModalFormValues {
  platform: PlatformType;
  platformCredential: string;
  platformPrincipal?: string;
  password?: string;
}

type Props = SubmitModalOwnProps &
  SubmitModalState &
  InjectedFormProps<
    SubmitModalFormValues,
    SubmitModalOwnProps,
    FormErrors<SubmitModalFormValues>
  >;

// Type Hack - https://stackoverflow.com/questions/49749060/
const FieldCustom = Field as unknown as new () => GenericField<any>;

const trimAndRemoveNl = (value?: string) =>
  value?.trim().replace(/(?:\r\n|\r|\n)/g, '');

const validate = (values: SubmitModalFormValues) => {
  const errors: FormErrors<SubmitModalFormValues> = {};

  switch (values.platform) {
    case 'APNS':
    case 'APNS_SANDBOX':
      if (!trimAndRemoveNl(values.platformCredential))
        errors.platformCredential = 'Required';
      if (!trimAndRemoveNl(values.platformPrincipal))
        errors.platformPrincipal = 'Required';
      break;
    case 'GCM':
      if (!trimAndRemoveNl(values.platformCredential))
        errors.platformCredential = 'Required';
      break;
    default:
      errors.platform = 'Required';
      break;
  }

  return errors;
};

const FORM_NAME = 'submitPlatformForm';
const PLATFORM_ITEMS = ALL_PLATFORMS.map((p) => ({
  id: p,
  name: mapPlatform(p),
}));

function SubmitModalImpl(props: Props): JSX.Element {
  const notify = useNotifications();
  const [loading, setLoading] = useState(false);
  const {
    certPassword,
    change,
    unavailablePlatforms,
    editPlatform,
    messengerId,
    show,
    handleSubmit,
    closeModal,
    refreshItems,
    destroy: destroyForm,
    selectedPlatform,
  } = props;

  useEffect(() => {
    if (!show) {
      destroyForm();
    }
  }, [destroyForm, show]);

  const isDuplicatePlatform =
    selectedPlatform && !editPlatform
      ? unavailablePlatforms.includes(selectedPlatform)
      : false;
  const platformItems = editPlatform
    ? PLATFORM_ITEMS
    : PLATFORM_ITEMS.filter((x) => !unavailablePlatforms.includes(x.id));

  const handleFormSubmit = async (values: SubmitModalFormValues) => {
    if (isDuplicatePlatform || loading || props.invalid) {
      return;
    }

    ampli.pushNotificationPlatformFormSubmitted({
      mode: editPlatform ? 'update' : 'create',
    });

    setLoading(true);

    const { platform, platformCredential, platformPrincipal } = values;

    try {
      await ajaxSend({
        path: `/v1/messengers/${messengerId}/platform_application`,
        method: 'PUT',
        data: {
          platform: platform,
          platformCredential,
          platformPrincipal,
        },
      });

      closeModal();
      refreshItems();

      notify({
        title: 'Push notification platform was submitted',
        text: `"${platform}" push notification platform was submitted.`,
        icon: 'done',
        className: 'success',
        time: 5000,
      });
    } catch (e) {
      console.error('Failed to submit push notification platform', e);

      notify({
        title: 'An error occurred',
        text: `An error occurred when trying to submit the push notification platform.`,
        icon: 'exclamation',
        className: 'danger',
      });
    }

    setLoading(false);
  };

  const resetForm = () => {
    change('platformCredential', '');
    change('platformPrincipal', '');
    change('password', '');
  };

  const controlsForPlatform = (): JSX.Element | null => {
    switch (selectedPlatform) {
      case 'GCM':
        return <GcmControls change={change} />;
      case 'APNS':
      case 'APNS_SANDBOX':
        return (
          <ApnsControls
            addNotification={notify}
            certPassword={certPassword}
            change={change}
            resetForm={resetForm}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal isOpen={show} onClose={closeModal}>
      <Box width="800px">
        <ModalHead
          title={
            editPlatform
              ? `Edit "${mapPlatform(editPlatform)}" platform`
              : 'Add a new push notification platform'
          }
          onClose={closeModal}
        />
        <Form onSubmit={handleSubmit(handleFormSubmit)}>
          <ModalBody>
            <FieldCustom
              component={FieldSelect}
              items={platformItems}
              label="Platform"
              name="platform"
              disabled={!!editPlatform}
              placeholder="Select platform"
              required
              onChange={(_, newValue, prevValue) => {
                if (newValue !== prevValue) {
                  // Reset form on platform switch
                  resetForm();
                }
              }}
            />
            {controlsForPlatform()}
          </ModalBody>
          <ModalFoot
            cancelOnClick={closeModal}
            confirmName="Submit platform"
            confirmOnClick={handleSubmit(handleFormSubmit)}
            confirmProps={{
              type: 'submit',
              disabled: isDuplicatePlatform || props.invalid,
              isLoading: loading,
            }}
          />
        </Form>
      </Box>
    </Modal>
  );
}

const valueSelector = formValueSelector(FORM_NAME);

const mapStateToProps = (state, ownProps: SubmitModalOwnProps) => ({
  selectedPlatform: valueSelector(state, 'platform'),
  certPassword: valueSelector(state, 'password'),
  initialValues: ownProps.editPlatform
    ? { platform: ownProps.editPlatform }
    : {},
});

export const SubmitModal = connect(mapStateToProps)(
  reduxForm<SubmitModalFormValues, SubmitModalOwnProps, FormErrors>({
    enableReinitialize: true,
    form: FORM_NAME,
    touchOnBlur: false,
    shouldError: () => true,
    validate,
  })(connect(mapStateToProps)(SubmitModalImpl)),
);
